import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import {
  AuthWrapper,
  FormWrapper,
  ContentLogin,
  ImageWrapper,
  Form,
  ConfirmWrapp,
  LinkWrap,
  EmptyColumn,
  ErrorMessage,
} from "../../style/Auth";

import logo from "../../images/ic_logo.svg";
import TextInput from "../../components/inputs/TextInput";
import Button from "../../components/button/index";
import PlayerLoader from "../../components/PlayerLoader";

import { verifyEmailToken, resendVerification } from "./api";
import { userActions } from "../../store/user/userSlice";
import { useLexicon } from "../../hooks/useLexicon";

const interp = (tmpl, nodes) => {
  const parts = String(tmpl).split(/\{[^}]+\}/);
  return parts.flatMap((p, i) => (i < nodes.length ? [p, nodes[i]] : [p]));
};

// /player/verify-email/:token — consumes the magic link, stores the JWT
// returned by the API, and routes the user to /player/setup-card. On
// failure offers a "resend" form so an expired link can be replaced.
const PlayerVerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const l = useLexicon();
  const calledRef = useRef(false);

  const [state, setState] = useState({ loading: true, error: null });
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState(null);

  useEffect(() => {
    if (!token || calledRef.current) return;
    calledRef.current = true;

    (async () => {
      try {
        const res = await verifyEmailToken(token);
        const { token: jwt, user } = res?.data ?? {};
        if (!jwt) throw new Error("No token in response");

        localStorage.setItem("token", jwt);
        const userType = user?.user_type || "player";
        const roles =
          Array.isArray(user?.roles) && user.roles.length > 0
            ? user.roles
            : [userType];
        localStorage.setItem("user_type", userType);
        localStorage.setItem("user_roles", JSON.stringify(roles));

        dispatch(userActions.setToken?.(jwt) ?? { type: "noop" });
        if (userActions.setAuthContext) {
          dispatch(
            userActions.setAuthContext({
              user_type: userType,
              roles,
              mapped_player_id: null,
              organization_id: null,
              subscription: null,
            })
          );
        }

        navigate("/player/setup-card", { replace: true });
      } catch (err) {
        const code = err?.response?.data?.code;
        setState({
          loading: false,
          error:
            code === "TOKEN_INVALID"
              ? l(1754)
              : err?.response?.data?.error || l(1755),
        });
      }
    })();
  }, [token, navigate, dispatch]);

  const onResend = async (event) => {
    event?.preventDefault();
    if (!resendEmail) return;
    setResending(true);
    try {
      await resendVerification(resendEmail);
    } catch {
      // 200-on-unknown semantics from the API; we always confirm.
    } finally {
      setResentAt(new Date());
      setResending(false);
    }
  };

  if (state.loading) {
    return (
      <AuthWrapper>
        <FormWrapper>
          <ContentLogin>
            <EmptyColumn />
            <Form as="div">
              <a href="/">
                <img className="logo" src={logo} alt="Logo" />
              </a>
              <h1>{l(1756)}</h1>
              <p>{l(1757)}</p>
              <PlayerLoader label={l(1758)} />
            </Form>
            <LinkWrap />
          </ContentLogin>
        </FormWrapper>
        <ImageWrapper />
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper>
      <FormWrapper>
        <ContentLogin>
          <EmptyColumn />
          <Form onSubmit={onResend}>
            <a href="/">
              <img className="logo" src={logo} alt="Logo" />
            </a>
            <h1>{l(1759)}</h1>
            <p>{state.error}</p>

            <TextInput
              label={l(1760)}
              type="email"
              value={resendEmail}
              placeholder="you@example.com"
              onChange={setResendEmail}
            />

            <ConfirmWrapp>
              <Button
                title={resending ? l(1731) : l(1761)}
                size="full"
                sizeResponse="full"
                type="submit"
                disabled={resending || !resendEmail}
              />
              {resentAt && <Hint>{l(1762)}</Hint>}
            </ConfirmWrapp>
          </Form>
          <LinkWrap>
            <h3>
              <Link to="/player/register">{l(1763)}</Link>
            </h3>
          </LinkWrap>
        </ContentLogin>
      </FormWrapper>
      <ImageWrapper />
    </AuthWrapper>
  );
};

export default PlayerVerifyEmail;

// /player/verify-email-pending — landing page reachable via the FE 402
// interceptor's EMAIL_NOT_VERIFIED branch and after register submission.
export const PlayerVerifyEmailPending = () => {
  const l = useLexicon();
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState(null);

  const onResend = async (event) => {
    event?.preventDefault();
    if (!email) return;
    setResending(true);
    try {
      await resendVerification(email);
    } catch {
      // Same 200-on-unknown semantics; always confirm.
    } finally {
      setResentAt(new Date());
      setResending(false);
    }
  };

  return (
    <AuthWrapper>
      <FormWrapper>
        <ContentLogin>
          <EmptyColumn />
          <Form onSubmit={onResend}>
            <a href="/">
              <img className="logo" src={logo} alt="Logo" />
            </a>
            <h1>{l(1728)}</h1>
            <p>{interp(l(1764), [<strong key="t">{l(1729)}</strong>])}</p>

            <TextInput
              label={l(1765)}
              type="email"
              value={email}
              placeholder="you@example.com"
              onChange={setEmail}
            />

            <ConfirmWrapp>
              <Button
                title={resending ? l(1731) : l(1730)}
                size="full"
                sizeResponse="full"
                type="submit"
                disabled={resending || !email}
              />
              {resentAt && (
                <Hint>{interp(l(1766), [resentAt.toLocaleTimeString()])}</Hint>
              )}
            </ConfirmWrapp>
          </Form>
          <LinkWrap>
            <h3>
              <Link to="/login">{l(1767)}</Link>
            </h3>
          </LinkWrap>
        </ContentLogin>
      </FormWrapper>
      <ImageWrapper />
    </AuthWrapper>
  );
};

const Hint = styled.span`
  display: inline-block;
  margin-top: 10px;
  color: ${(props) => props.theme.blueish2};
  font-family: "SatoshiRegular", sans-serif;
  font-size: 12px;
`;
